import React, { memo } from 'react';

/**
 * Badge - A small pill/tag component for labels, counts, and status indicators
 * 
 * @example
 * <Badge variant="blue">5 מילים</Badge>
 * <Badge variant="green" size="sm">✓ הושלם</Badge>
 */

export type BadgeVariant = 'blue' | 'green' | 'yellow' | 'purple' | 'orange' | 'red' | 'gray';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  children: React.ReactNode;
  /** Color variant */
  variant?: BadgeVariant;
  /** Size preset */
  size?: BadgeSize;
  /** Additional CSS classes */
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  gray: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1',
  md: 'text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5',
};

export const Badge: React.FC<BadgeProps> = memo(({
  children,
  variant = 'blue',
  size = 'md',
  className = '',
}) => {
  return (
    <span
      className={`
        inline-flex items-center gap-1
        rounded-full
        font-heebo font-bold
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      {children}
    </span>
  );
});

Badge.displayName = 'Badge';
